import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { EyeOff, Pencil, SearchX, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import AppShell from "@/components/layout/AppShell";
import ConfirmDialog from "@/components/ConfirmDialog";
import StatusScreen from "@/components/StatusScreen";
import useOrganization from "../../hooks/useOrganization";
import { getBrickSeries, updateBrickSeries, deleteBrickSeries } from "../../utils";
import type { BrickSeries as Series } from "../../masterImages/types";
import BricksList from "./BricksList";
import MasterImages from "./MasterImages";
import EditNameModal from "./EditNameModal";
import UploadThumbnail from "./UploadThumbnail";

const LoadingState = () => (
  <div className="grid gap-4">
    <Skeleton className="h-52 rounded-xl" />
    <Skeleton className="h-64 rounded-xl" />
    <Skeleton className="h-96 rounded-xl" />
  </div>
);

const BrickSeries: React.FC = () => {
  const organizationId = useOrganization();
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const [series, setSeries] = useState<Series | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [updatingVisibility, setUpdatingVisibility] = useState(false);

  const fetchSeries = useCallback(async () => {
    try {
      setSeries(await getBrickSeries(organizationId, seriesId));
    } catch (e) {
      setLoadError((e as Error).message);
    }
  }, [organizationId, seriesId]);

  useEffect(() => {
    fetchSeries();
  }, [fetchSeries]);

  const handleDelete = async () => {
    try {
      await deleteBrickSeries(organizationId, seriesId);
      toast.success("Collectie verwijderd");
      navigate(`/${organizationId}`);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const setVisible = async (visible: boolean) => {
    setUpdatingVisibility(true);
    try {
      await updateBrickSeries(organizationId, seriesId, { hidden: !visible });
      setSeries((current) => current && { ...current, hidden: !visible });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setUpdatingVisibility(false);
    }
  };

  const bricks = series?.bricks.length ?? 0;
  const masterImages = series?.masterImages?.length ?? 0;

  return (
    <AppShell
      breadcrumbs={[
        { label: "Collecties", to: `/${organizationId}` },
        { label: series?.name ?? "Collectie" },
      ]}
    >
      {loadError ? (
        <StatusScreen
          icon={SearchX}
          title="Deze collectie kan niet worden geladen"
          description={loadError}
          actions={
            <Button variant="outline" onClick={() => navigate(`/${organizationId}`)}>
              Naar collecties
            </Button>
          }
        />
      ) : (
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-4 md:p-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="grid gap-1">
              {series ? (
                <h1 className="flex flex-wrap items-center gap-2 text-2xl font-semibold tracking-tight">
                  {series.name}
                  {series.hidden && (
                    <Badge variant="outline" className="text-muted-foreground">
                      <EyeOff />
                      Verborgen
                    </Badge>
                  )}
                </h1>
              ) : (
                <Skeleton className="h-8 w-64" />
              )}
              <p className="text-sm text-muted-foreground">
                {series
                  ? `${bricks} ${bricks === 1 ? "steen" : "stenen"} · ${masterImages} master ${masterImages === 1 ? "image" : "images"}`
                  : "Collectie laden…"}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" disabled={!series} onClick={() => setRenaming(true)}>
                <Pencil />
                Naam wijzigen
              </Button>
              <ConfirmDialog
                title="Deze collectie verwijderen?"
                description="De collectie en al haar stenen verdwijnen ook uit de widget. Dit kan niet ongedaan worden gemaakt."
                confirmLabel="Verwijderen"
                onConfirm={handleDelete}
                trigger={
                  <Button
                    variant="outline"
                    disabled={!series}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 />
                    Verwijderen
                  </Button>
                }
              />
            </div>
          </div>

          {series == null ? (
            <LoadingState />
          ) : (
            <>
              <Card>
                <CardContent className="flex flex-wrap items-start gap-6">
                  <UploadThumbnail brickSeries={series} onUpdate={setSeries} />
                  <div className="grid min-w-0 flex-1 gap-5 sm:max-w-md">
                    <div className="grid gap-1">
                      <h2 className="font-semibold">Thumbnail</h2>
                      <p className="text-sm text-muted-foreground">
                        Het plaatje waarmee de collectie in de widget en in deze lijst verschijnt.
                      </p>
                    </div>
                    <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
                      <div className="grid gap-1">
                        <Label htmlFor="series-visible">Zichtbaar in de widget</Label>
                        <p className="text-sm text-muted-foreground">
                          Verborgen collecties blijven bewaard, maar klanten zien ze niet.
                        </p>
                      </div>
                      <Switch
                        id="series-visible"
                        checked={!series.hidden}
                        disabled={updatingVisibility}
                        onCheckedChange={setVisible}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <MasterImages brickSeries={series} onChange={fetchSeries} />
              <BricksList brickSeries={series} onChange={fetchSeries} />
            </>
          )}
        </div>
      )}
      {series && seriesId && (
        <EditNameModal
          organization={organizationId}
          id={seriesId}
          currentName={series.name}
          open={renaming}
          onClose={(renamed) => {
            setRenaming(false);
            if (renamed) fetchSeries();
          }}
        />
      )}
    </AppShell>
  );
};

export default BrickSeries;
